import {
    Spinner,
    Field,
    Dropdown,
    Option,
    Text,
    Textarea,
} from '@fluentui/react-components';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SubmitButton from '../../../controls/Buttons/SubmitButton';
import { OnOpenChangeCallback } from '../../../hooks/useDialog';
import { useAppContext } from '../../../model';
import { IBrokerConnectionActiveConversationsData } from '../../../model/brokerConnections/IBrokerConnectionActiveConversationsData';
import IConnectToTeam from '../../../model/IConnectToTeam';
import {
    transferConversation,
    activeConversationLengths,
} from '../../../store/features/activeConversations/activeConversationsAPI';
import { loadActiveConversations } from '../../../store/features/activeConversations/activeConversationsSlice';
import { getConnectToTeams } from '../../../store/features/help/helpSlice';
import { useAppDispatch, useSelector } from '../../../store/hooks';
import { useTransferConversationFormStyles } from './TransferConversationForm.styles';

export interface TransferConversationFormProps {
    conversationData: IBrokerConnectionActiveConversationsData | null;
    onSuccess?: () => void;
    dialog?: {
        open: boolean;
        onOpenChange: OnOpenChangeCallback;
    };
}

const TransferConversationForm: React.FC<TransferConversationFormProps> = ({
    conversationData,
    onSuccess,
    dialog,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const styles = useTransferConversationFormStyles();

    const { connectToTeams, connectToTeamsStatus } = useSelector((x) => x.help);
    const isLoading = connectToTeamsStatus !== 'idle';

    const { teamsContext } = useAppContext();
    const currentTeamId = teamsContext.team?.groupId?.toUpperCase() ?? '';

    const dialogOpen = dialog?.open;
    const onDialogOpenChange = dialog?.onOpenChange;

    const availableTeams = useMemo(
        () =>
            connectToTeams.filter(
                (x) =>
                    x.isContactable === true &&
                    x.teamId.toUpperCase() !== currentTeamId
            ),
        [connectToTeams, currentTeamId]
    );
    const [selectedTeam, setSelectedTeam] = useState<IConnectToTeam | null>(
        null
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [note, setNote] = useState('');

    useEffect(() => {
        if (dialogOpen === false) {
            setSelectedTeam(null);
            setNote('');
            return;
        }

        dispatch(getConnectToTeams());
    }, [dialogOpen, dispatch]);

    const onTeamSelect = useCallback(
        (key?: string) => {
            if (!key) {
                return;
            }

            const foundTeam = availableTeams.find((x) => x.key === key);

            if (!foundTeam) {
                return;
            }

            setSelectedTeam(foundTeam);
        },
        [availableTeams]
    );

    const onSubmitCallback = useCallback(() => {
        if (!conversationData || !selectedTeam || isSubmitting || isLoading) {
            return;
        }

        setIsSubmitting(true);

        transferConversation(conversationData.id, {
            targetTeam: {
                teamName: selectedTeam.teamName,
                teamId: selectedTeam.teamId,
                channelName: selectedTeam.channelName,
                channelId: selectedTeam.channelId,
            },
            note,
        }).then(() => {
            setIsSubmitting(false);

            if (onSuccess) {
                onSuccess();
            }

            if (onDialogOpenChange) {
                onDialogOpenChange(false);
                dispatch(loadActiveConversations());
            }
        });
    }, [
        conversationData,
        onDialogOpenChange,
        selectedTeam,
        isSubmitting,
        note,
        isLoading,
        dispatch,
        onSuccess,
    ]);

    return isLoading ? (
        <Spinner />
    ) : (
        <>
            <div className={styles.form}>
                <Field label={t('SelectTeamPlaceholder')}>
                    <Dropdown
                        value={selectedTeam?.title ?? ''}
                        onOptionSelect={(_, data) =>
                            onTeamSelect(data.optionValue)
                        }
                        selectedOptions={selectedTeam ? [selectedTeam.key] : []}
                        disabled={isLoading}
                    >
                        {availableTeams.map((x) => (
                            <Option key={x.key} value={x.key}>
                                {x.title}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
                <Field label={t('ActiveConversations_TransferNote')}>
                    <Textarea
                        value={note}
                        onChange={(_, data) =>
                            setNote(
                                data.value.slice(
                                    0,
                                    activeConversationLengths.note.max
                                )
                            )
                        }
                    />
                    <Text size={200} italic>
                        {note.length}/{activeConversationLengths.note.max}
                    </Text>
                </Field>
            </div>
            <SubmitButton
                disabled={
                    !conversationData ||
                    !selectedTeam ||
                    isSubmitting ||
                    isLoading
                }
                onSubmit={onSubmitCallback}
                buttonProps={{ className: styles.button }}
            />
        </>
    );
};

export default TransferConversationForm;
